import {createSlice, createSelector} from '@reduxjs/toolkit';
import {accountsApi, MyAccount, MyCompany} from '../services/accounts';
import {removeAccessToken, storeAccessToken} from '../cookies';
import {RootState} from '../store';
import { Tool } from './plans/types';
import { toolsApi } from '../services/tools';
import { vendorProfileApi } from '../services/vendorProfile';
import { companiesApi } from '../services/companies';

export interface AccountState {
	token: string | null;
	account: MyAccount | null;
	company: MyCompany | null;
	tools: Tool[];
	isVerifyEmailDialogOpen: boolean;
}

const initialState: AccountState = {
	token: null,
	account: null,
	company: null,
	tools: [],
	isVerifyEmailDialogOpen: false,
};

const accountSlice = createSlice({
	name: 'account',
	initialState,
	reducers: {
		tokenSetState(state, action) {
			state.token = action.payload;
			return state;
		},
		resetState() {
			removeAccessToken();
			return initialState;
		},
		isVerifyEmailDialogOpenSetState(state, action) {
			state.isVerifyEmailDialogOpen = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(accountsApi.endpoints.swapToken.matchFulfilled, (state, action) => {
				state.token = action.payload.token;
				state.account = null;
				state.company = null;
				storeAccessToken(action.payload.token);
			})
			.addMatcher(accountsApi.endpoints.getMyAccount.matchFulfilled, (state, action) => {
				const {company, ...account} = action.payload;
				state.account = account;
				state.company = company;
			})
			.addMatcher(accountsApi.endpoints.getMyAccount.matchRejected, accountSlice.caseReducers.resetState) // triggers token swap
			.addMatcher(accountsApi.endpoints.updateMyCompany.matchFulfilled, (state, action) => {
				state.company = {...state.company, ...action.payload} as MyCompany;
			})
			.addMatcher(toolsApi.endpoints.getCompanyTools.matchFulfilled, (state, action) => {
				state.tools = action.payload;
			})
			.addMatcher(toolsApi.endpoints.addCompanyTool.matchFulfilled, (state, action) => {
				state.tools = [...state.tools, action.payload];
			})
			.addMatcher(toolsApi.endpoints.removeCompanyTool.matchFulfilled, (state, action) => {
				state.tools = state.tools.filter((tool) => tool.id !== action.payload.id);
			})
			.addMatcher(vendorProfileApi.endpoints.activateVendorProfile.matchFulfilled, (state) => {
				state.company = {...state.company, roleVendor: true} as MyCompany;
			})
			.addMatcher(vendorProfileApi.endpoints.deactivateVendorProfile.matchFulfilled, (state) => {
				state.company = {...state.company, roleVendor: false} as MyCompany;
			})
			.addMatcher(companiesApi.endpoints.uploadCompanyImage.matchFulfilled, (state, action) => {
				state.company = {...state.company, imgUrl: action.payload.imgUrl} as MyCompany;
			})
			.addMatcher(companiesApi.endpoints.uploadCompanyCoverImage.matchFulfilled, (state, action) => {
				state.company = {...state.company, coverImgUrl: action.payload.coverImgUrl} as MyCompany;
			})
			.addMatcher(accountsApi.endpoints.activateCreatorProfile.matchFulfilled, (state, action) => {
				state.company = {...state.company, ...action.payload} as MyCompany;
			});
	},
});

export const {
	tokenSetState,
	resetState,
	isVerifyEmailDialogOpenSetState,
} = accountSlice.actions;

const selectAccountSlice = (state: RootState) => state.account;

export const selectAccessToken = createSelector([selectAccountSlice], (state: AccountState) => state.token);

export const selectUserProfile = createSelector([selectAccountSlice], (state: AccountState) => state.account);

export const selectMyCompany = createSelector([selectAccountSlice], (state: AccountState) => state.company);

export const selectIsAuthenticated = createSelector([selectAccountSlice], (state: AccountState) => state.account ? true : false);

export const selectIsVerifyEmailDialogOpen = createSelector([selectAccountSlice], (state: AccountState) => state.isVerifyEmailDialogOpen);

export const selectCompanyTools = createSelector([selectAccountSlice], (state: AccountState) => state.tools);

export default accountSlice.reducer;
